/**
 *
 * Entrypoint
 *
 */

import { Auth0Provider } from '@auth0/auth0-react'
import { lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider as ReduxProvider } from 'react-redux'
import { createBrowserRouter, Navigate, RouterProvider, useNavigate } from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'
import feLogger, { initFeLogger } from './lib/feLogger'

import AuthGuard from '~/comps/AuthGuard'
import Loader from '~/comps/Loader'
import RouteErrorBoundary from '~/comps/RouteErrorBoundary'
import UnsupportedBrowser from '~/comps/UnsupportedBrowser'
import { isSupportedBrowser } from '~/lib/getBrowserName'
import { initOsScrollbars } from '~/lib/osScrollbars'
import { initAssetResolver } from '~/lib/resolveAssetUrl'
import '~/lib/tools'
import { isMobile, isStandalone } from '~/routes/mobile/pwaUtils'
import Root from '~/routes/root'
import store, { persistor } from '~/store'
import { selectSessionId } from '~/store/api/selectors'
import '~/styles/index.scss'
import { AUTH_AUDIENCE, AUTH_CLIENTID, AUTH_DOMAIN, IS_DEBUG, IS_LOCAL, IS_MOBILE } from './lib/constants'
import getRollbarContext from './lib/getRollbarContext'

initAssetResolver(store)

initFeLogger(store, selectSessionId, getRollbarContext)

// Lazy load routes
const AppDesktop = lazy(() => import('~/routes/app'))
const AppMobile = lazy(() => import('~/routes/mobile'))
const App = IS_MOBILE ? AppMobile : AppDesktop
const Credits = lazy(() => import('~/routes/credits'))
const Callback = lazy(() => import('~/routes/callback'))
const Login = lazy(() => import('~/routes/login'))
const Output = lazy(() => import('~/routes/output'))
const CameraRoute = lazy(() => import('~/routes/camera'))
// Lazy on purpose: the chunk inlines ~75KB of markdown (see routes/guides).
const Guides = lazy(() => import('~/routes/guides'))
const InstallBlocker = lazy(() => import('~/routes/mobile/InstallBlocker'))

// The transition rig (`__rig`) and the UI helpers (`__ui`) arm themselves on every dev load, so they are there without pasting them in. They live outside `src/`, and the dynamic specifier is what keeps them out of the production bundle.
if (IS_LOCAL) {
	import(/* @vite-ignore */ '/tests/manual/rig.js').catch(e => console.warn('[rig] not armed:', e?.message ?? e))
	import(/* @vite-ignore */ '/tests/manual/ui.js').catch(e => console.warn('[ui] not armed:', e?.message ?? e))
	import('~/lib/paletteLab')
	import('~/lib/virtualMidi')
}

// Global error handlers → feLogger (skip on localhost to avoid HMR noise)
if (!IS_LOCAL) {
	window.onerror = (message, source, lineno, colno, error) => {
		feLogger.reportError(error || new Error(message), { source, lineno, colno })
	}
	window.onunhandledrejection = event => {
		const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
		feLogger.reportError(error)
	}
}

const AuthRoot = () => {
	const navigate = useNavigate()

	const onRedirectCallback = appState => {
		navigate(appState?.returnTo || window.location.pathname)
	}

	return (
		<Auth0Provider
			domain={AUTH_DOMAIN}
			clientId={AUTH_CLIENTID}
			authorizationParams={{
				redirect_uri: window.location.origin + '/callback',
				audience: AUTH_AUDIENCE,
			}}
			onRedirectCallback={onRedirectCallback}
			cacheLocation='localstorage'
			useRefreshTokens={true}
		>
			<Root />
		</Auth0Provider>
	)
}

/**
 * MobileGate - Pre-auth check for mobile PWA installation
 * Shows InstallBlocker BEFORE requiring auth, so users only need to log in once
 * after installing the app to their home screen.
 */
const MobileGate = () => {
	// Check if we need to show install blocker (mobile browser, not installed)
	// Skip in local or with debug flag to allow testing with DevTools device emulator
	const needsInstall = !IS_DEBUG && isMobile() && !isStandalone()

	if (needsInstall) {
		return (
			<Suspense fallback={<Loader />}>
				<InstallBlocker />
			</Suspense>
		)
	}

	// Already installed or not mobile - proceed to auth-protected app
	return <AuthGuard component={App} />
}

const Gate = IS_MOBILE ? MobileGate : () => <AuthGuard component={App} />

const router = createBrowserRouter([
	{
		path: '/camera',
		element: (
			<Suspense fallback={<Loader />}>
				<CameraRoute />
			</Suspense>
		),
	},
	{
		path: '/output',
		element: (
			<Suspense fallback={<Loader />}>
				<Output />
			</Suspense>
		),
	},
	// Public docs page. A TOP-LEVEL sibling, not a child of '/', deliberately:
	// Root's replaceState (routes/root/index.jsx) rewrites the URL to `?name=xxxx`
	// once authenticated, which would drop the path and break every /guides#anchor
	// deep link. Staying out here also skips Auth0, so the page needs no login.
	{
		path: '/guides',
		element: (
			<Suspense fallback={<Loader />}>
				<Guides />
			</Suspense>
		),
	},
	{
		path: '/',
		element: <AuthRoot />,
		errorElement: <RouteErrorBoundary />,
		children: [
			{
				index: true,
				element: <Gate />,
			},
			{
				path: 'callback',
				element: <Callback />,
			},
			{
				path: 'login',
				element: (
					<Suspense fallback={<Loader />}>
						<Login />
					</Suspense>
				),
			},
			{
				path: 'p_credits',
				element: <AuthGuard component={Credits} />,
			},
			{
				path: 'about',
				element: <Gate />,
			},
			{
				path: 'credits',
				element: <Gate />,
			},
			{
				path: 'feedback',
				element: <Gate />,
			},
			{
				path: 'assets',
				element: <Gate />,
			},
			{
				path: 'assets/:mode',
				element: <Gate />,
			},
			{
				path: 'recordings',
				element: <Gate />,
			},
			{
				path: 'settings',
				element: <Gate />,
			},
			{
				path: 'settings/:page',
				element: <Gate />,
			},
			{
				path: 'settings/:page/:sub',
				element: <Gate />,
			},
			{
				path: 'player',
				element: <Gate />,
			},
			{
				path: 'image-viewer',
				element: <Gate />,
			},
			{
				path: 'profile',
				element: <Gate />,
			},
			{
				path: 'templates',
				element: <Gate />,
			},
			{
				path: 'new-template',
				element: <Gate />,
			},
			{
				path: 'edit-template/:id',
				element: <Gate />,
			},
			{
				path: 'save-template-as',
				element: <Gate />,
			},
			{
				path: '*',
				element: <Navigate to='/' replace />,
			},
		],
	},
])

// TODO: Remove RollbarWrapper once Rollbar packages are removed
// const RollbarWrapper = ({ children }) => { ... }

// // Measure native scrollbar width and set CSS variable
// const scrollbarProbe = document.createElement('div')
// scrollbarProbe.style.cssText = 'overflow:scroll;width:100px;height:100px;position:absolute;top:-9999px'
// document.body.appendChild(scrollbarProbe)
// document.documentElement.style.setProperty('--scrollbar-width', (scrollbarProbe.offsetWidth - scrollbarProbe.clientWidth) + 'px')
// scrollbarProbe.remove()

const root = createRoot(document.getElementById('root'))

if (!IS_MOBILE && !isSupportedBrowser()) {
	root.render(<UnsupportedBrowser />)
} else {
	root.render(
		<ReduxProvider store={store}>
			<PersistGate loading={null} persistor={persistor}>
				<RouterProvider router={router} future={{ v7_startTransition: true }} />
			</PersistGate>
		</ReduxProvider>
	)

	// Overlay scrollbars on desktop only (mobile keeps native touch scroll).
	if (!IS_MOBILE) initOsScrollbars()
}
